import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Grid,
  Chip,
  Paper,
  Button,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StorageIcon from "@mui/icons-material/Storage";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import TerminalIcon from "@mui/icons-material/Terminal";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HistoryIcon from "@mui/icons-material/History";
import SettingsIcon from "@mui/icons-material/Settings";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { IBackupTarefaV2 } from "./types";
import { TarefasApi } from "./services/tarefasApi";
import { useLogin } from "../../contexts/LoginContext";
import { formatarDataHoraCurta, formatarDataCurta } from "../../common/utils/formatadorDataHora";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";

const verificarSeSucesso = (status: string | undefined): boolean => {
  if (!status) return false;
  const s = status.toLowerCase();
  return (
    s === "sucesso" ||
    s === "concluido" ||
    s === "concluído" ||
    s === "executado" ||
    s === "executada" ||
    s === "salvolocalmente" ||
    s === "salvo localmente"
  );
};

const renderChipUltimaExecucao = (status: string | undefined) => {
  if (!status) return null;
  const s = status.toLowerCase();
  if (s === "salvolocalmente" || s === "salvo localmente" || s.includes("salvo")) {
    return (
      <Chip
        label="Salvo Localmente"
        color="info"
        size="small"
        sx={{ height: 20, mt: 0.5, fontWeight: "bold" }}
      />
    );
  }
  const sucesso = verificarSeSucesso(status);
  return (
    <Chip
      label={sucesso ? "Sucesso" : "Falha"}
      color={sucesso ? "success" : "error"}
      size="small"
      sx={{ height: 20, mt: 0.5, fontWeight: "bold" }}
    />
  );
};

interface IDetalhesTarefaProps {
  tarefa: IBackupTarefaV2 | null;
  open: boolean;
  onClose: () => void;
}

export const DetalhesTarefa: React.FC<IDetalhesTarefaProps> = ({
  tarefa,
  open,
  onClose
}) => {
  const [login] = useLogin();
  const [tarefaCompleta, setTarefaCompleta] = useState<IBackupTarefaV2 | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [executandoRemoto, setExecutandoRemoto] = useState(false);
  const [feedback, setFeedback] = useState<{ tipo: "success" | "error"; msg: string } | null>(null);

  useEffect(() => {
    if (!open || !tarefa) {
      setTarefaCompleta(null);
      return;
    }

    const tarefaId = tarefa.tarefaAgendadaID || (tarefa as any).id;
    if (!tarefaId || !login.accessToken) {
      setTarefaCompleta(tarefa);
      return;
    }

    setCarregando(true);
    TarefasApi.carregarTarefa(tarefaId, login.accessToken)
      .then((dados) => {
        setTarefaCompleta(dados ? { ...tarefa, ...dados } : tarefa);
      })
      .catch((err) => {
        console.error("Erro ao carregar dados completos da tarefa:", err);
        setTarefaCompleta(tarefa);
      })
      .finally(() => {
        setCarregando(false);
      });
  }, [open, tarefa, login.accessToken]);

  if (!tarefa) return null;
  const t = tarefaCompleta || tarefa;

  const handleCopiarId = () => {
    const id = t.tarefaAgendadaID || (t as any).id;
    if (id) {
      navigator.clipboard.writeText(id);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    }
  };

  const handleExecutarRemoto = async () => {
    const id = t.tarefaAgendadaID || (t as any).id;
    if (!id || !login.accessToken) return;
    setExecutandoRemoto(true);
    try {
      const res = await TarefasApi.executarTarefaRemota(id, login.accessToken);
      setFeedback({ tipo: "success", msg: res.mensagem || "Ordem de execução manual enviada com sucesso para o agente!" });
    } catch (err: any) {
      setFeedback({ tipo: "error", msg: err?.response?.data?.mensagem || "Erro ao solicitar execução manual." });
    } finally {
      setExecutandoRemoto(false);
    }
  };

  const obterMotorBanco = (t: IBackupTarefaV2) => {
    if (t.tipo === "SQLServer" || t.tipo === "Postgres" || t.tipo === "Sybase") {
      return t.tipo;
    }
    if (t.tipo === "CheckDB" && t.configBancoDados?.tipo) {
      const motor = t.configBancoDados.tipo;
      if (motor === "SQLServer" || motor === "Postgres" || motor === "Sybase") {
        return motor;
      }
    }
    return null;
  };

  const renderInstrucoesRestauracao = (motor: string) => {
    const dbName = tarefa.configBancoDados?.bancoDados || "NOME_DO_BANCO";
    const host = tarefa.configBancoDados?.servidor || "SERVIDOR";
    const port = tarefa.configBancoDados?.porta || "5432";
    const user = tarefa.configBancoDados?.usuario || "USUARIO";

    if (motor === "SQLServer") {
      const code = `-- 1. Colocar banco em modo de usuário único para derrubar conexões ativas\n` +
        `USE [master];\n` +
        `ALTER DATABASE [${dbName}] SET SINGLE_USER WITH ROLLBACK IMMEDIATE;\n\n` +
        `-- 2. Executar restauração (substitua o caminho do arquivo físico local baixado)\n` +
        `RESTORE DATABASE [${dbName}]\n` +
        `FROM DISK = N'C:\\Caminho\\Para\\Backup\\${dbName}.bak'\n` +
        `WITH REPLACE, RECOVERY;\n\n` +
        `-- 3. Voltar banco para modo multiusuário\n` +
        `ALTER DATABASE [${dbName}] SET MULTI_USER;`;

      return (
        <Box>
          <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
            Execute o script T-SQL abaixo no SSMS (SQL Server Management Studio) para restaurar a base de dados:
          </Typography>
          <Box sx={{ borderRadius: "8px", overflow: "hidden", fontSize: "11px", border: "1px solid rgba(0,0,0,0.1)" }}>
            <SyntaxHighlighter language="sql" customStyle={{ margin: 0, padding: "12px" }}>
              {code}
            </SyntaxHighlighter>
          </Box>
        </Box>
      );
    }

    if (motor === "Postgres") {
      const code = `# Restaurar usando pg_restore (para formatos Custom/Tar - padrão do Backup Fácil)\n` +
        `pg_restore --host="${host}" --port="${port}" --username="${user}" --dbname="${dbName}" --verbose --clean --no-owner "Caminho/Para/Arquivo.backup"\n\n` +
        `# Ou restaurar usando psql (se for dump em formato texto SQL simples)\n` +
        `psql -h ${host} -p ${port} -U ${user} -d ${dbName} -f "Caminho/Para/Arquivo.sql"`;

      return (
        <Box>
          <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
            Execute os comandos abaixo no terminal (Windows PowerShell / Linux Bash) para restaurar a base de dados:
          </Typography>
          <Box sx={{ borderRadius: "8px", overflow: "hidden", fontSize: "11px", border: "1px solid rgba(0,0,0,0.1)" }}>
            <SyntaxHighlighter language="bash" customStyle={{ margin: 0, padding: "12px" }}>
              {code}
            </SyntaxHighlighter>
          </Box>
        </Box>
      );
    }

    if (motor === "Sybase") {
      const code = `# 1. Restaurar banco de dados SAP SQL Anywhere usando utilitário dbbackup\n` +
        `dbbackup -c "uid=${user};eng=${host};dbn=${dbName}" -y "Diretorio/De/Destino"\n\n` +
        `# Nota: Certifique-se de que o serviço do banco esteja parado caso esteja substituindo a base ativa de mesmo nome.`;

      return (
        <Box>
          <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
            Execute o comando utilitário abaixo no prompt de comando do Sybase/SQL Anywhere:
          </Typography>
          <Box sx={{ borderRadius: "8px", overflow: "hidden", fontSize: "11px", border: "1px solid rgba(0,0,0,0.1)" }}>
            <SyntaxHighlighter language="bash" customStyle={{ margin: 0, padding: "12px" }}>
              {code}
            </SyntaxHighlighter>
          </Box>
        </Box>
      );
    }

    return null;
  };

  const motor = obterMotorBanco(t);

  const renderIconeTipo = (tipoTarefa: string) => {
    switch (tipoTarefa) {
      case "SQLServer":
      case "Postgres":
      case "Sybase":
        return <StorageIcon color="primary" sx={{ fontSize: 32 }} />;
      case "CheckDB":
        return <FactCheckIcon color="primary" sx={{ fontSize: 32 }} />;
      case "Arquivos":
        return <InsertDriveFileIcon color="primary" sx={{ fontSize: 32 }} />;
      case "Bat":
        return <TerminalIcon color="primary" sx={{ fontSize: 32 }} />;
      default:
        return null;
    }
  };

  const renderBadgeStatus = (statusTarefa: string) => {
    switch (statusTarefa) {
      case "ativa":
        return <Chip label="Ativa e Protegida" color="success" size="small" />;
      case "pausada":
        return <Chip label="Pausada" color="default" size="small" />;
      case "erro_configuracao":
        return <Chip label="Erro de Configuração" color="warning" size="small" />;
      case "inativa":
        return <Chip label="Inativa" color="error" size="small" />;
      default:
        return <Chip label={statusTarefa || "Ativa"} color="success" size="small" />;
    }
  };

  const formatarAgendamentoDetalhado = (item: IBackupTarefaV2) => {
    const freq = item.tipoAgendamento || "Diariamente";
    const hora = item.horaExecucao || (item.agendamento as any)?.horaExecucao || "20:00:00";

    if (item.agendamento) {
      const details = item.agendamento;
      if (freq === "Semanalmente" && (details as any).dias) {
        const diasSemana = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
        const diasNomes = ((details as any).dias as number[]).map((d) => diasSemana[d]).join(", ");
        return `Semanalmente (${diasNomes}) às ${hora}`;
      }
      if (freq === "Mensalmente" && (details as any).dias && (details as any).meses) {
        const mesesNomes = [
          "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
          "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
        ];
        const mesesText = ((details as any).meses as number[]).map((m) => mesesNomes[m - 1]).join(", ");
        const diasText = ((details as any).dias as number[]).join(", ");
        return `Mensalmente (Dias: ${diasText} dos meses: ${mesesText}) às ${hora}`;
      }
      if (freq === "Repetidamente" && (details as any).intervalo) {
        const intVal = (details as any).intervalo;
        const unidade = (details as any).tipoIntervalo === "Hora" ? "hora(s)" : "minuto(s)";
        return `A cada ${intVal} ${unidade}`;
      }
    }

    if (freq === "UmaVez") {
      return `Execução única agendada para às ${hora}`;
    }

    if (freq === "Diariamente" || freq === "Diario") {
      return `Diariamente às ${hora}`;
    }

    return `${freq} às ${hora}`;
  };

  const idTarefa = t.tarefaAgendadaID || (t as any).id;
  const configBanco = t.configBancoDados;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 580 }, p: 3, borderTopLeftRadius: "16px", borderBottomLeftRadius: "16px" }
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flexGrow: 1, minWidth: 0, pr: 1 }}>
          <Box sx={{ mt: 0.5 }}>{renderIconeTipo(t.tipo)}</Box>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, wordBreak: "break-word" }}>
              {t.nome}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all", fontWeight: 500 }}>
                ID: {idTarefa}
              </Typography>
              <Tooltip title={copiado ? "Copiado!" : "Copiar ID da tarefa"}>
                <IconButton size="small" onClick={handleCopiarId} sx={{ p: 0.3 }}>
                  {copiado ? <CheckIcon sx={{ fontSize: 14, color: "success.main" }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2.5 }} />

      {carregando && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, p: 1, bgcolor: "rgba(42, 81, 247, 0.05)", borderRadius: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="primary">Atualizando detalhes completos...</Typography>
        </Box>
      )}

      <Grid container spacing={2.5}>
        {/* Bloco de Status Geral */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "rgba(0,0,0,0.01)", flexWrap: "wrap", gap: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Status da Tarefa
              </Typography>
              <Box sx={{ mt: 0.5 }}>{renderBadgeStatus(t.status)}</Box>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Plataforma
              </Typography>
              {t.plataforma === "linux" ? (
                <Chip
                  label="🐧 Linux (Ubuntu)"
                  color="info"
                  variant="outlined"
                  size="small"
                  sx={{ mt: 0.5, fontWeight: 600 }}
                />
              ) : (
                <Chip
                  label="🪟 Windows"
                  variant="outlined"
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Grupo de Backup
              </Typography>
              <Chip 
                label={t.grupoNome || (typeof t.grupo === "object" && t.grupo ? (t.grupo as any).nome : t.grupo) || "Sem Grupo"} 
                variant="outlined" 
                size="small" 
                sx={{ mt: 0.5 }} 
              />
            </Box>
          </Paper>
        </Grid>

        {/* Agendamento */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <CalendarTodayIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Agendamento e Período
            </Typography>
          </Box>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Frequência: <span style={{ fontWeight: 400 }}>{formatarAgendamentoDetalhado(t)}</span>
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Data de Início
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t.dataInicio ? formatarDataCurta(t.dataInicio) : "Imediato"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Data de Término
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t.dataFim ? formatarDataCurta(t.dataFim) : "Sem expiração (Contínuo)"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Histórico e Execuções */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <HistoryIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Histórico de Execução
            </Typography>
          </Box>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Última Execução
                </Typography>
                {t.ultimaExecucao ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatarDataHoraCurta(t.ultimaExecucao.dataHora)}
                    </Typography>
                    {renderChipUltimaExecucao(t.ultimaExecucao.status)}
                    {t.ultimaExecucao.tamanhoFinalBytes !== undefined && t.ultimaExecucao.tamanhoFinalBytes > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Tamanho: {(t.ultimaExecucao.tamanhoFinalBytes / (1024 * 1024)).toFixed(2)} MB
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Nunca executou
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Próxima Execução Prevista
                </Typography>
                {t.proximaExecucao ? (
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {formatarDataHoraCurta(t.proximaExecucao.dataHora)}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Sem agendamento futuro
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Configurações Técnicas de Origem/Destino */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <SettingsIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Parâmetros de Origem e Destino
            </Typography>
          </Box>
          <Paper sx={{ p: 2 }}>
            {/* Se for Banco de dados */}
            {(t.tipo === "SQLServer" || t.tipo === "Postgres" || t.tipo === "Sybase" || t.tipo === "CheckDB") && (
              configBanco ? (
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Banco de Dados
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{configBanco.bancoDados || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Servidor / Instância
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{configBanco.servidor || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Mecanismo / Tipo
                    </Typography>
                    <Typography variant="body2">{configBanco.tipo || t.tipo}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Porta de Conexão
                    </Typography>
                    <Typography variant="body2">{configBanco.porta || "Padrão"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Usuário de Acesso
                    </Typography>
                    <Typography variant="body2">{configBanco.usuario || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Senha
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                      •••••••• (Protegida)
                    </Typography>
                  </Grid>
                  {configBanco.descricao && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Descrição da Conexão
                      </Typography>
                      <Typography variant="body2">{configBanco.descricao}</Typography>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                  Configuração de banco de dados vinculada à tarefa.
                </Typography>
              )
            )}

            {/* Se for arquivos */}
            {t.tipo === "Arquivos" && (
              t.arquivos ? (
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Pasta / Diretório de Origem
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-all", fontFamily: "monospace", fontWeight: 500 }}>
                      {t.arquivos.pastaOrigem || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Pasta de Destino Local
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-all", fontFamily: "monospace", fontWeight: 500 }}>
                      {t.arquivos.pastaDestino || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Chip
                      label={t.arquivos.incluirSubPastas ? "Inclui todas as subpastas" : "Apenas arquivos da pasta raiz"}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                  Origem e destino de pastas configurados.
                </Typography>
              )
            )}

            {/* Se for Script BAT */}
            {t.tipo === "Bat" && (
              t.batch ? (
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Diretório de Destino dos Logs
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-all", fontFamily: "monospace", mb: 1.5 }}>
                      {t.batch.pastaDestino || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Script BAT Executável
                    </Typography>
                    <Box sx={{ borderRadius: "8px", overflow: "hidden", fontSize: "12px", border: "1px solid rgba(0,0,0,0.1)" }}>
                      <SyntaxHighlighter language="batch" customStyle={{ margin: 0, padding: "12px" }}>
                        {t.batch.comando || "@echo off\n:: Sem comando cadastrado"}
                      </SyntaxHighlighter>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                  Script executável configurado.
                </Typography>
              )
            )}
          </Paper>
        </Grid>

        {/* Como Restaurar */}
        {motor && (
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <IntegrationInstructionsIcon color="action" fontSize="small" />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Como Restaurar este Banco (Instruções)
              </Typography>
            </Box>
            <Paper sx={{ p: 2 }}>
              {renderInstrucoesRestauracao(motor)}
            </Paper>
          </Grid>
        )}

        {/* Ações Pós-Execução */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <IntegrationInstructionsIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Ações Pós-Execução
            </Typography>
          </Box>
          <Paper sx={{ p: 2 }}>
            {(!t.posExecucao || t.posExecucao.length === 0) ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                Nenhuma ação pós-execução configurada para esta tarefa.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {t.posExecucao.map((acao, idx) => (
                  <Box key={idx} sx={{ display: "flex", flexDirection: "column", gap: 0.5, p: 1.5, borderRadius: "6px", backgroundColor: "rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.06)" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {acao.tipo === "HistoricoBackupData7"
                          ? "Integração Data7 ERP"
                          : acao.tipo === "Bat"
                          ? "Executar Script .bat"
                          : "Executar Comando do Sistema"}
                      </Typography>
                      <Chip
                        label={acao.situacaoExecucao === "Executada" || !acao.situacaoExecucao ? "Ativo" : acao.situacaoExecucao}
                        color="success"
                        size="small"
                        sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
                      />
                    </Box>
                    {acao.tipo === "HistoricoBackupData7" && (
                      <Typography variant="caption" color="text.secondary">
                        Registra automaticamente o sucesso e as métricas do backup na base do Data7 ERP.
                      </Typography>
                    )}
                    {acao.tipo === "Bat" && acao.caminhoBat && (
                      <Typography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                        Caminho do Script: {acao.caminhoBat}
                      </Typography>
                    )}
                    {acao.tipo === "ComandoBat" && acao.comandoBat && (
                      <Typography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                        Comando: {acao.comandoBat}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleExecutarRemoto}
          disabled={executandoRemoto}
          startIcon={executandoRemoto ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
          sx={{ borderRadius: "8px" }}
        >
          {executandoRemoto ? "Enviando comando..." : "Executar Tarefa Agora"}
        </Button>
        <Button variant="outlined" onClick={onClose} sx={{ borderRadius: "8px" }}>
          Fechar Painel
        </Button>
      </Box>

      {/* Feedback Snackbar */}
      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={6000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback(null)}
          severity={feedback?.tipo || "info"}
          sx={{ width: "100%", borderRadius: "8px" }}
        >
          {feedback?.msg}
        </Alert>
      </Snackbar>
    </Drawer>
  );
};

export default DetalhesTarefa;
